import { ArrowRight } from 'lucide-react';

import {
    Card,
    CardContent,
    CardDescription,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { usePriceVisible } from '@/components/ui/price';
import { formatTimeAgo } from '@/lib/shared-utils';
import { cn } from '@/lib/utils';

const formatDate = (dateString: string): string => formatTimeAgo(dateString);

const STATUS_STYLES: Record<string, string> = {
    completed: 'bg-success/10 text-success',
    confirmed: 'bg-success/10 text-success',
    accepted: 'bg-success/10 text-success',
    processing: 'bg-primary/10 text-primary',
    pending: 'bg-warning/10 text-warning',
    cancelled: 'bg-destructive/10 text-destructive',
    declined: 'bg-destructive/10 text-destructive',
};

function StatusBadge({ status, statusDisplay }: { status: string; statusDisplay: string }) {
    const className = STATUS_STYLES[status] ?? 'bg-muted text-muted-foreground';
    return (
        <span className={cn('inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold', className)}>
            {statusDisplay}
        </span>
    );
}

interface Order {
    id: number;
    order_uuid: string;
    order_email: string;
    customer_name: string;
    amount: string;
    status: string;
    status_display: string;
    created_at: string;
    code?: string;
}

interface RecentOrdersCardProps {
    orders: Order[];
    onSeeAll?: () => void;
}

export function RecentOrdersCard({ orders, onSeeAll }: RecentOrdersCardProps) {
    const priceVisible = usePriceVisible();
    return (
        <Card className="lg:col-span-3 gap-0 rounded-xl border-border/70 py-0 shadow-xs">
            <CardHeader className="flex! items-start justify-between gap-2 px-5 pt-5 pb-4">
                <div className="space-y-1">
                    <CardTitle className="text-base font-semibold">
                        Recent Orders
                    </CardTitle>
                    <CardDescription className="text-xs">
                        Latest transactions across all channels.
                    </CardDescription>
                </div>
                <button
                    type="button"
                    onClick={onSeeAll}
                    className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
                >
                    See all
                    <ArrowRight className="h-3.5 w-3.5" />
                </button>
            </CardHeader>
            <CardContent className="px-2 pb-2">
                <table className="w-full">
                    <thead>
                        <tr className="border-b border-border/60">
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Order ID</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-muted-foreground">Customer</th>
                            <th className="hidden px-3 py-2 text-left text-xs font-medium text-muted-foreground sm:table-cell">Date</th>
                            {priceVisible && (
                                <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Amount</th>
                            )}
                            <th className="px-3 py-2 text-right text-xs font-medium text-muted-foreground">Status</th>
                        </tr>
                    </thead>
                    <tbody>
                        {orders.slice(0, 5).map((order) => (
                            <tr
                                key={order.id}
                                className="border-b border-border/40 last:border-0 transition-colors hover:bg-muted/40"
                            >
                                <td className="px-3 py-4 font-mono text-xs tracking-tight text-muted-foreground">
                                    {order.code ?? `#${order.order_uuid.slice(0, 8)}`}
                                </td>
                                <td className="px-3 py-4">
                                    <span className="block max-w-[160px] truncate text-sm font-semibold text-foreground">
                                        {order.customer_name || order.order_email}
                                    </span>
                                </td>
                                <td className="hidden px-3 py-4 text-sm text-muted-foreground sm:table-cell">
                                    {formatDate(order.created_at)}
                                </td>
                                {priceVisible && (
                                    <td className="px-3 py-4 text-right text-sm font-semibold text-foreground tabular-nums">
                                        $
                                        {order.amount}
                                    </td>
                                )}
                                <td className="px-3 py-4 text-right">
                                    <StatusBadge
                                        status={order.status}
                                        statusDisplay={order.status_display}
                                    />
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </CardContent>
        </Card>
    );
}
